'use client';

import { useState, useEffect, useRef } from 'react';
import type { Locale } from '../HomeLanding';

interface Service {
  id: string;
  code: string;
  title: string;
  tagline: string;
  description: string;
  deliverables: string[];
}

function ServiceCard({
  service,
  index,
  isVietnamese,
  isIndonesian,
  isChinese,
}: {
  service: Service;
  index: number;
  isVietnamese: boolean;
  isIndonesian: boolean;
  isChinese: boolean;
}) {
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!cardRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              entry.target.classList.add('active');
            }, index * 80);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 }
    );
    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [index]);

  return (
    <div ref={cardRef} className="reveal group h-full">
      <div className="h-full rounded-md border border-divider bg-surface p-7 shadow-none transition-all duration-300 group-hover:border-blue/30 group-hover:shadow-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded flex items-center justify-center bg-blue-light text-blue">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 3c3 4 5 7 5 10a5 5 0 0 1-10 0c0-3 2-6 5-10Z" />
                <path d="M9 14c1 1 2 1.5 3 1.5s2-.5 3-1.5" />
              </svg>
            </div>
            <span className="font-mono text-[10px] text-titanium tracking-[0.12em] block">{service.code}</span>
          </div>
          <span className="font-mono text-[10px] text-blue font-medium">Pillar {service.code.slice(1)}</span>
        </div>

        <h3 className="mt-5 font-semibold text-charcoal text-[17px] tracking-tight leading-snug">{service.title}</h3>

        <p className="mt-3 text-titanium text-[13px] leading-relaxed">{service.description}</p>

        <div className="mt-5 flex flex-wrap gap-1.5">
          {service.deliverables.map((deliverable) => (
            <span
              key={deliverable}
              className="font-mono text-[10px] text-titanium bg-surface-alt border border-divider px-2 py-0.5 rounded-sm tracking-wide"
            >
              {deliverable}
            </span>
          ))}
        </div>

        <div className="mt-6 pt-5 border-t border-divider/70 flex items-center justify-between gap-4">
          <p className="text-[12px] text-blue font-semibold tracking-wide">{service.tagline}</p>
          <span className="text-[12px] font-medium text-titanium flex items-center gap-1 shrink-0">
            {isVietnamese ? 'Xem chi tiết' : isIndonesian ? 'Lihat detail' : isChinese ? '查看详情' : 'View details'}
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </div>
      </div>
    </div>
  );
}

export default function ServicesGrid({ locale = 'en' }: { locale?: Locale }) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isVietnamese = locale === 'vn';
  const isIndonesian = locale === 'id';
  const isChinese = locale === 'cn';

  const services: Service[] = isVietnamese
    ? [
        {
          id: 'purity-assurance',
          code: 'P01',
          title: 'Đảm bảo độ tinh khiết',
          tagline: 'Độ M&I < 1% được cam kết.',
          description: 'Lọc nhiều giai đoạn và loại bỏ độ ẩm để đảm bảo tạp chất ở mức tối thiểu.',
          deliverables: [
            'Sàng lọc độ ẩm và tạp chất không tan',
            'Xác minh lô hàng theo ngưỡng tinh khiết',
            'Báo cáo lô cuối cho khâu mua hàng',
          ],
        },
        {
          id: 'lipid-profiling',
          code: 'P02',
          title: 'Phân tích lipid',
          tagline: 'Báo cáo GC theo từng lô.',
          description: 'Phân tích chi tiết EPA, DHA và Omega-3 được điều chỉnh cho từng giai đoạn tăng trưởng của vật nuôi.',
          deliverables: [
            'Tóm tắt hồ sơ EPA / DHA / Omega-3',
            'Báo cáo lô bằng sắc ký khí',
            'Bảng tham chiếu công thức theo giai đoạn',
          ],
        },
        {
          id: 'oxidation-control',
          code: 'P03',
          title: 'Kiểm soát oxy hóa',
          tagline: 'Độ ổn định PV và AV thấp.',
          description: 'Được ổn định bằng chất chống oxy hóa chất lượng cao để ngăn ôi khét và duy trì độ ngon miệng lâu dài.',
          deliverables: [
            'Giám sát chỉ số peroxide',
            'Kiểm tra độ ổn định chỉ số anisidine',
            'Hướng dẫn giữ lại chất chống oxy hóa',
          ],
        },
        {
          id: 'feed-efficiency-enhancement',
          code: 'P04',
          title: 'Nâng cao hiệu quả thức ăn',
          tagline: 'Mật độ năng lượng 9.0+ kcal/g.',
          description: 'Cải thiện FCR thông qua nguồn năng lượng dễ tiêu hóa cho thức ăn thủy sản và chăn nuôi.',
          deliverables: [
            'Tham chiếu mật độ năng lượng cho nhà máy thức ăn',
            'Hướng dẫn cung ứng theo khả năng tiêu hóa',
            'Ghi chú hỗ trợ FCR cho đội công thức',
          ],
        },
        {
          id: 'export-compliance',
          code: 'P05',
          title: 'Tuân thủ xuất khẩu',
          tagline: 'Được chứng nhận GMP+, HACCP và Phytosanitary.',
          description: 'Hậu cần và chứng từ đầy đủ để giao dịch quốc tế liền mạch và truy xuất nguồn gốc.',
          deliverables: [
            'Bộ chứng từ xuất khẩu',
            'Nhật ký truy xuất và tuân thủ',
            'Tóm tắt nhận dạng lô hàng sẵn sàng giao dịch',
          ],
        },
        {
          id: 'sustainable-sourcing',
          code: 'P06',
          title: 'Nguồn cung bền vững',
          tagline: 'Nguồn cung đạt chuẩn IFFO RS / MarinTrust.',
          description: 'Nguyên liệu được khai thác có trách nhiệm từ các ngư trường được kiểm toán để hỗ trợ sản xuất thức ăn thân thiện môi trường.',
          deliverables: [
            'Tóm tắt nguồn cung từ ngư trường kiểm toán',
            'Tài liệu mua hàng có trách nhiệm',
            'Tuyên bố truy xuất nguồn gốc bền vững',
          ],
        },
      ]
    : isIndonesian
      ? [
          {
            id: 'purity-assurance',
            code: 'P01',
            title: 'Jaminan kemurnian',
            tagline: 'Verifikasi standar kemurnian pada setiap batch.',
            description: 'Penyaringan multi-tahap dan penghilangan kadar air untuk memastikan kadar pengotor (impurities) seminimal mungkin.',
            deliverables: [
              'Penyaringan kadar air dan pengotor tak larut',
              'Verifikasi standar kemurnian pada setiap batch',
              'Laporan analisis akhir untuk tinjauan pembelian',
            ],
          },
          {
            id: 'lipid-profiling',
            code: 'P02',
            title: 'Profil lipid',
            tagline: 'Laporan analisis GC per batch.',
            description: 'Analisis detail kandungan EPA, DHA, dan Omega-3 yang disesuaikan untuk tahap pertumbuhan ternak dan akuakultur.',
            deliverables: [
              'Ringkasan profil EPA / DHA / Omega-3',
              'Laporan analisis Kromatografi Gas (GC)',
              'Lembar referensi formulasi per tahap',
            ],
          },
          {
            id: 'oxidation-control',
            code: 'P03',
            title: 'Kontrol oksidasi',
            tagline: 'Stabilitas nilai PV dan AV rendah.',
            description: 'Distabilkan dengan antioksidan berkualitas tinggi untuk mencegah ketengikan dan menjaga palatabilitas jangka panjang.',
            deliverables: [
              'Pemantauan nilai peroxide',
              'Pengecekan stabilitas nilai anisidine',
              'Stabilitas nilai Peroxide (PV) dan Anisidine (AV) yang rendah',
            ],
          },
          {
            id: 'feed-efficiency-enhancement',
            code: 'P04',
            title: 'Peningkatan efisiensi pakan',
            tagline: 'Panduan formulasi berdasarkan daya cerna.',
            description: 'Meningkatkan efisiensi FCR melalui sumber energi yang mudah dicerna untuk pakan akuakultur dan ternak.',
            deliverables: [
              'Referensi kepadatan energi untuk pabrik pakan',
              'Panduan formulasi berdasarkan daya cerna (digestibility)',
              'Catatan dukungan FCR untuk tim formulasi',
            ],
          },
          {
            id: 'export-compliance',
            code: 'P05',
            title: 'Kepatuhan ekspor',
            tagline: 'Identifikasi batch siap kirim.',
            description: 'Logistik dan dokumentasi yang sepenuhnya tersertifikasi untuk kelancaran perdagangan internasional dengan sistem ketertelusuran (traceability) yang akurat.',
            deliverables: [
              'Paket dokumen ekspor',
              'Log pelacakan dan kepatuhan',
              'Identifikasi Batch Ready-to-Ship',
            ],
          },
          {
            id: 'sustainable-sourcing',
            code: 'P06',
            title: 'Sumber berkelanjutan',
            tagline: 'Sertifikasi ketertelusuran berkelanjutan.',
            description: 'Bahan baku didapatkan secara bertanggung jawab dari tata kelola perikanan yang teraudit untuk mendukung keberlanjutan produksi pakan.',
            deliverables: [
              'Ringkasan sumber perikanan yang diaudit',
              'Dokumentasi pengadaan yang bertanggung jawab',
              'Sertifikasi Ketertelusuran Berkelanjutan',
            ],
          },
        ]
      : isChinese
        ? [
            {
              id: 'purity-assurance',
              code: 'P01',
              title: '纯度保障',
              tagline: '保证 M&I < 1%。',
              description: '多级过滤和脱水，确保杂质保持在最低水平。',
              deliverables: ['水分与不溶性杂质筛查', '按纯度阈值进行批次放行验证', '供采购审核的最终批次报告'],
            },
            {
              id: 'lipid-profiling',
              code: 'P02',
              title: '脂质分析',
              tagline: '按批次提供 GC 分析报告。',
              description: '针对特定生长阶段，对 EPA、DHA 和 Omega-3 含量进行详细分析。',
              deliverables: ['EPA / DHA / Omega-3 概览', '气相色谱批次报告', '分阶段配方参考表'],
            },
            {
              id: 'oxidation-control',
              code: 'P03',
              title: '氧化控制',
              tagline: '低 PV / AV 稳定性。',
              description: '采用高品质抗氧化剂稳定产品，防止酸败并保持长期适口性。',
              deliverables: ['过氧化值监测', '苯胺值稳定性检查', '抗氧化剂保留指导'],
            },
            {
              id: 'feed-efficiency-enhancement',
              code: 'P04',
              title: '提升饲料效率',
              tagline: '9.0+ kcal/g 能量密度。',
              description: '通过高消化率能源来源提升 FCR，适用于水产与畜牧饲料。',
              deliverables: ['饲料厂能量密度参考', '以消化率为导向的供货建议', '供配方团队参考的 FCR 支持说明'],
            },
            {
              id: 'export-compliance',
              code: 'P05',
              title: '出口合规',
              tagline: 'GMP+、HACCP 和植物检疫认证。',
              description: '完整合规的物流与文件，支持无缝国际贸易与追溯。',
              deliverables: ['出口文件包', '追溯与合规记录', '可直接交易的批次识别摘要'],
            },
            {
              id: 'sustainable-sourcing',
              code: 'P06',
              title: '可持续采购',
              tagline: '符合 IFFO RS / MarinTrust 的采购来源。',
              description: '来自经审核渔场的负责任原料，支持环保型饲料生产。',
              deliverables: ['经审核渔场来源摘要', '负责任采购文件', '可持续追溯声明'],
            },
          ]
        : [
            {
              id: 'purity-assurance',
              code: 'P01',
              title: 'Purity Assurance',
              tagline: '< 1% M&I guaranteed.',
              description: 'Multi-stage filtration and moisture removal to ensure minimal impurities.',
              deliverables: [
                'Moisture and insoluble impurities screening',
                'Verifikasi standar kemurnian pada setiap batch',
                'Final lot report for procurement review',
              ],
            },
            {
              id: 'lipid-profiling',
              code: 'P02',
              title: 'Lipid Profiling',
              tagline: 'Batch-specific GC analysis reports.',
              description: 'Detailed analysis of EPA, DHA, and Omega-3 content tailored for specific animal growth stages.',
              deliverables: ['EPA / DHA / Omega-3 profile summary', 'Gas chromatography batch report', 'Stage-specific formulation reference sheet'],
            },
            {
              id: 'oxidation-control',
              code: 'P03',
              title: 'Oxidation Control',
              tagline: 'Low PV and AV stability.',
              description: 'Stabilized with high-quality antioxidants to prevent rancidity and ensure long-term palatability.',
              deliverables: ['Peroxide value monitoring', 'Anisidine value stability check', 'Antioxidant retention guidance'],
            },
            {
              id: 'feed-efficiency-enhancement',
              code: 'P04',
              title: 'Feed Efficiency Enhancement',
              tagline: '9.0+ kcal/g energy density.',
              description: 'Improving FCR through highly digestible energy sources for aquatic and livestock feed.',
              deliverables: ['Energy density reference for feed mills', 'Digestibility-oriented supply guidance', 'FCR support note for formulation teams'],
            },
            {
              id: 'export-compliance',
              code: 'P05',
              title: 'Export Compliance',
              tagline: 'GMP+, HACCP, and Phytosanitary certified.',
              description: 'Fully certified logistics and documentation for seamless international trade and traceability.',
              deliverables: ['Export documentation pack', 'Traceability and compliance log', 'Trade-ready lot identification summary'],
            },
            {
              id: 'sustainable-sourcing',
              code: 'P06',
              title: 'Sustainable Sourcing',
              tagline: 'IFFO RS / MarinTrust compliant sourcing.',
              description: 'Responsibly sourced raw materials from audited fisheries to support eco-friendly feed production.',
              deliverables: ['Audited fishery sourcing summary', 'Responsible procurement documentation', 'Sustainability traceability statement'],
            },
          ];

  useEffect(() => {
    if (!sectionRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.section-reveal').forEach((el, i) => {
              setTimeout(() => el.classList.add('active'), i * 100);
            });
          }
        });
      },
      { threshold: 0.05 }
    );
    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" ref={sectionRef} className="py-24 bg-canvas scroll-mt-20" aria-label="Service catalogue">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="flex items-center gap-3 mb-4 section-reveal reveal">
              <span className="inline-block w-6 h-px bg-blue" />
              <span className="font-mono text-[11px] tracking-[0.18em] text-blue uppercase font-medium">
                {isVietnamese ? 'Danh mục dịch vụ' : isIndonesian ? 'Katalog layanan' : isChinese ? '服务目录' : 'Service Catalogue'}
              </span>
            </div>
            <h2 className="section-reveal reveal reveal-delay-1 font-bold tracking-[-0.04em] text-charcoal leading-tight" style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)' }}>
              {isVietnamese ? 'Sáu trụ cột.' : isIndonesian ? 'Enam pilar,' : isChinese ? '六大支柱。' : 'Six pillars.'}
              <br />
              <span className="text-titanium font-light">
                {isVietnamese ? 'Một chất lượng đồng nhất.' : isIndonesian ? 'untuk satu standar kualitas yang konsisten.' : isChinese ? '一致品质，稳定如一。' : 'One consistent quality.'}
              </span>
            </h2>
          </div>
          <p className="section-reveal reveal reveal-delay-2 text-titanium text-[15px] leading-relaxed max-w-sm">
            {isVietnamese
              ? 'Nhấp vào bất kỳ thẻ nào để xem thông số kỹ thuật, độ ổn định chống oxy hóa và hồ sơ axit béo phía sau mỗi lô hàng.'
              : isIndonesian
                ? 'Klik kartu mana pun untuk melihat spesifikasi teknis, stabilitas antioksidan, dan profil asam lemak di balik setiap pengiriman.'
                : isChinese
                  ? '点击任一卡片，即可查看技术参数、抗氧化稳定性，以及每批货物背后的脂肪酸概况。'
                  : 'Click any card to reveal technical specifications, antioxidant stability, and the fatty acid profile behind every shipment.'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((service, index) => (
            <ServiceCard
              key={service.id}
              service={service}
              index={index}
              isVietnamese={isVietnamese}
              isIndonesian={isIndonesian}
              isChinese={isChinese}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
